import classNames from 'classnames';
import { BEMBlock } from 'helpers/classnames';
import { forwardRef, useState } from 'react';
import * as React from 'react';
import { ProductPricesCollection } from 'palette/candidates/ProductPrices/types';
import { usePaletteConfig } from 'palette/Config';
import { useModal } from 'palette/Modal';
import SplashDialog from 'palette/ProductCard/ProductCardBlocks/SplashDialog';
import { ProductSplashes } from 'palette/ProductSplashes';
import { ComingSoonSplash } from 'palette/ProductSplashes/ComingSoonSplash';
import { Toggle } from 'palette/Toggle';
import { DEFAULT_TAG_NAME, TagName } from 'palette/Typography';
import Attributes from './ProductCardBlocks/Attributes';
import Description from './ProductCardBlocks/Description';
import ImageSplashes from './ProductCardBlocks/ImageSplashes';
import ProductCardMainSection from './ProductCardBlocks/ProductCardMainSection';
import { useProductCardContext } from './ProductCardContext';
import { OnCardClick, OnCardLinkClick, IDataAttributes } from './types/cardElements';
import {
    IProductData, Thumbnail, IPrices, IImageSplash, ISplashTooltip, IImageObject,
} from './types/productData';

import './styles/ProductCard.scss';

export interface ProductCardProps {
    productData: IProductData;
    prices: IPrices;
    productPricesStructure: Record<number | string, ProductPricesCollection[]>;
    thumbnails: Thumbnail[];
    topHeaderComponent?: React.ReactNode;
    customDescriptionComponent?: React.ReactNode;
    dataAttributes?: IDataAttributes;
    actionButtons?: React.ReactElement[];
    favoriteButton?: React.ReactElement;
    onDisabledNotificationClick?: React.MouseEventHandler<HTMLButtonElement>;
    onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
    onClick?: OnCardClick;
    onLinkClick?: OnCardLinkClick;
    onLinkContextMenu?: OnCardLinkClick;
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
    className?: string;
    brandnameFieldTagname?: TagName;
    isSelected?: boolean;
    allowVideo?: boolean;
    modelImageMode?: boolean;
    carouselImages?: IImageObject[];
    scrollIndicatorAnimation?: boolean;
    hasToShowFavoriteButtonLast?: boolean;
}

const ProductCard = forwardRef(({
    productData,
    prices,
    productPricesStructure,
    thumbnails,
    topHeaderComponent,
    actionButtons,
    favoriteButton,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onLinkClick,
    onLinkContextMenu,
    onMouseDown,
    dataAttributes,
    className = '',
    brandnameFieldTagname = DEFAULT_TAG_NAME.span,
    isSelected = false,
    allowVideo = false,
    modelImageMode = false,
    scrollIndicatorAnimation = false,
    customDescriptionComponent = null,
    carouselImages = [],
    hasToShowFavoriteButtonLast = false,
}: ProductCardProps, ref: any) => {
    const [tooltip, setTooltip] = useState<ISplashTooltip | null>(null);
    const { setActiveModalId } = useModal();
    const { theme: paletteTheme, device } = usePaletteConfig();

    const {
        orientation,
        size,
        setIsHovered,
        isSelectable,
        isDisabled,
        activeVariantId,
        setActiveVariantId,
    } = useProductCardContext();

    const {
        attributes = [],
        alternativeImage,
        brandName,
        colors,
        productName,
        teaserDate,
        altText,
        media,
    } = productData;
    const video = media?.find(({ type }) => type === 'video');
    const showTeaserSplash = teaserDate !== null;
    const showAttributes = (!isSelectable || isDisabled) && !showTeaserSplash;
    const isGreyedOut = isDisabled || (isSelectable && isSelected);
    const activeVariant = thumbnails.find(thumb => thumb.variantId === activeVariantId) ?? thumbnails[0];
    const { img, url, splashes } = activeVariant;
    const showSplashes = splashes && !isSelectable && !showTeaserSplash;
    const modalId = `variant_${activeVariantId}`;
    const [block, BEMElement] = BEMBlock('palette-product-card', {
        'greyed-out': isGreyedOut,
        [`${paletteTheme}`]: paletteTheme !== undefined,
        [orientation]: orientation !== undefined,
    });

    const mouseEnterHandler = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsHovered(true);
        onMouseEnter?.(event);
    };

    const mouseLeaveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
        setActiveVariantId(thumbnails[0].variantId);
        setIsHovered(false);
        onMouseLeave?.(event);
    };

    const handleSplashClick = (splash: IImageSplash) => {
        if (splash.tooltip) {
            setTooltip(splash.tooltip);
            setActiveModalId(modalId);
        }
    };

    const handleSplashClose = () => {
        setTooltip(null);
    };

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
        <div
            ref={ref}
            aria-label={productName}
            className={classNames(block, className)}
            role="article"
            tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
            onClick={onClick}
            {...dataAttributes && { ...dataAttributes }}
            {...device === 'desktop' && {
                onMouseEnter: mouseEnterHandler,
                onMouseLeave: mouseLeaveHandler,
                onMouseDown,
            }}
        >
            {topHeaderComponent && (
                topHeaderComponent
            )}
            {isSelectable && (
                <Toggle
                    className={BEMElement('select-toggle', { [orientation]: orientation !== undefined })}
                    id={`select-${activeVariantId}`}
                    isChecked={isSelected}
                />
            )}
            <ProductCardMainSection
                allowVideo={allowVideo}
                alternativeImage={alternativeImage}
                altText={altText ?? ''}
                carouselImages={carouselImages}
                image={img}
                modelImageMode={modelImageMode}
                productUrl={url}
                scrollIndicatorAnimation={scrollIndicatorAnimation}
                video={video}
                onClick={onLinkClick}
                onLinkContextMenu={onLinkContextMenu}
            >
                {showAttributes && (
                    <Attributes
                        attributes={attributes}
                        numberOfAttributes={1}
                    />
                )}
                {showSplashes && (
                    <div
                        className={BEMElement('splashes', {
                            [`${size}-card`]: size !== undefined,
                        })}
                    >
                        <ProductSplashes
                            className={BEMElement('splashes-box')}
                            splashes={splashes?.box}
                        />
                        <ImageSplashes
                            splashes={splashes?.image}
                            onClick={handleSplashClick}
                        />
                    </div>
                )}
                {showTeaserSplash && (
                    <ComingSoonSplash size={size} />
                )}
                {tooltip && (
                    <SplashDialog
                        body={tooltip.body}
                        id={modalId}
                        title={tooltip.title}
                        onClose={handleSplashClose}
                    />
                )}
            </ProductCardMainSection>
            {
                customDescriptionComponent ?? (
                    <Description
                        actionButtons={actionButtons}
                        brandName={brandName}
                        brandnameFieldTagname={brandnameFieldTagname}
                        colors={colors}
                        descriptionData={activeVariant}
                        favoriteButton={favoriteButton}
                        hasToShowFavoriteButtonLast={hasToShowFavoriteButtonLast}
                        prices={prices}
                        productName={productName}
                        productPricesStructure={productPricesStructure}
                        thumbnails={thumbnails}
                    />
                )
            }
        </div>
    );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard as (
    props: ProductCardProps & { ref?: any }
) => JSX.Element;
